import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";

// Path belongs to a module if it equals a route prefix or is nested under it.
const pathInRoutes = (path, routes) =>
  routes.some((r) => path === r || path.startsWith(r + "/"));

/**
 * Fetches the caller's module state once. Exposes helpers the sidebar uses to
 * dim locked items and the layout uses to gate locked pages.
 *
 * `authKey` should be a stable truthy value that is present whenever the user
 * is authenticated (e.g. user?.id). We deliberately do NOT depend on an
 * in-memory access token: auth is cookie-based (axios withCredentials), and the
 * token is null after any full page reload/deep link — depending on it would
 * silently disable gating.
 */
export function useModules(authKey) {
  const [catalog, setCatalog] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [locked, setLocked] = useState([]);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!authKey) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/modules`, { withCredentials: true });
        if (!alive) return;
        setCatalog(res.data?.catalog || []);
        setEnabled(res.data?.enabled || []);
        setLocked(res.data?.locked || []);
        setTrials(res.data?.trials || []);
      } catch (e) {
        // Fail-open on read error: never blackout the whole app because the
        // gating lookup failed. Locked stays empty => everything visible.
        if (alive) {
          setCatalog([]);
          setEnabled([]);
          setLocked([]);
          setTrials([]);
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [authKey]);

  // Returns the module object a path belongs to, or null (platform base).
  const moduleForPath = useCallback(
    (path) => catalog.find((m) => pathInRoutes(path, m.routes)) || null,
    [catalog],
  );

  // Returns the LOCKED module object for a path, or null.
  const lockedModuleForPath = useCallback(
    (path) => {
      const m = moduleForPath(path);
      return m && locked.includes(m.key) ? m : null;
    },
    [moduleForPath, locked],
  );

  const isPathLocked = useCallback(
    (path) => !!lockedModuleForPath(path),
    [lockedModuleForPath],
  );

  // Returns the active trial ({module, days_left}) for a path, or null.
  const trialForPath = useCallback(
    (path) => {
      const m = moduleForPath(path);
      if (!m) return null;
      return trials.find((t) => t.module === m.key) || null;
    },
    [moduleForPath, trials],
  );

  return {
    catalog, enabled, locked, trials, loading,
    moduleForPath, lockedModuleForPath, isPathLocked, trialForPath,
  };
}
